import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact waitlist opt-in shown on SOLD / private-collection artwork pages.
 * Tags the newsletter signup with the series slug so the studio can email
 * subscribers when a similar piece is available.
 */
export default function WaitlistCTA({ seriesSlug, seriesLabel, pieceTitle }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, {
        email,
        list: `waitlist-${seriesSlug}`,
      });
      toast.success(
        `On the ${seriesLabel} waitlist. You'll hear first when the next one lands.`
      );
      setEmail("");
      setSent(true);
    } catch {
      toast.error("Couldn't join the waitlist. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div
        className="mt-8 border border-foreground/30 p-5"
        data-testid="waitlist-sent"
      >
        <p className="label mb-2">On the waitlist</p>
        <p className="serif italic text-lg text-[hsl(var(--ink-soft))]">
          First look at the next {seriesLabel.toLowerCase()} piece — before it
          hits the public catalog.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 border border-foreground/30 p-5" data-testid="waitlist-cta">
      <p className="label mb-1">This piece is placed</p>
      <p className="serif text-xl md:text-2xl leading-snug mb-4">
        Want the next painting in this vein?
      </p>
      <p className="text-sm text-[hsl(var(--ink-soft))] mb-4">
        Get first look at the next {seriesLabel} piece before it&apos;s posted
        publicly.
      </p>
      <form onSubmit={submit} data-testid="waitlist-form">
        <div className="flex border-b border-border pb-2">
          <Mail className="h-5 w-5 self-center mr-3 opacity-60" />
          <input
            type="email"
            required
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-transparent outline-none placeholder:text-[hsl(var(--ink-soft))]/50 text-sm py-2"
            data-testid="waitlist-email-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 label"
            data-testid="waitlist-submit"
          >
            {busy ? "..." : "Join waitlist"}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </form>
    </div>
  );
}
